/**
 * AUTO-GENERATED from tokens.css — DO NOT EDIT
 *
 * Regenerar: npx tsx front/scripts/generate-tokens.ts
 */

/* eslint-disable sonarjs/no-hardcoded-passwords */

export const colorPrimitive = {
  blue: {
    50: "#e3f2fd",
    100: "#bbdefb",
    300: "#64b5f6",
    400: "#42a5f5",
    500: "#2196f3",
    600: "#1e88e5",
    700: "#1976d2",
    900: "#0d47a1",
  },
  purple: {
    50: "#f3e5f5",
    100: "#e1bee7",
    300: "#ba68c8",
    400: "#ab47bc",
    500: "#9c27b0",
    700: "#7b1fa2",
    900: "#4a148c",
    A400: "#d500f9",
  },
  neutral: {
    0: "#ffffff",
    50: "#fafafa",
    100: "#f5f5f5",
    200: "#eeeeee",
    300: "#e0e0e0",
    400: "#bdbdbd",
    500: "#9e9e9e",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  },
  success: {
    50: "#e8f5e9",
    100: "#c8e6c9",
    300: "#81c784",
    light: "#81c784",
    main: "#4caf50",
    dark: "#388e3c",
    "soft-bg": "#e8f5ee",
    "soft-text": "#006b3f",
    contrast: "#ffffff",
  },
  error: {
    50: "#ffebee",
    100: "#ffcdd2",
    300: "#e57373",
    light: "#ef5350",
    main: "#f44336",
    dark: "#d32f2f",
    "soft-bg": "#fee2e2",
    "soft-text": "#b91c1c",
    contrast: "#ffffff",
  },
  warning: {
    50: "#fff3e0",
    100: "#ffe0b2",
    300: "#ffb74d",
    light: "#ffb74d",
    main: "#ff9800",
    dark: "#f57c00",
    "soft-bg": "#fef3c7",
    "soft-text": "#d4580a",
    contrast: "#ffffff",
  },
  info: {
    50: "#e1f5fe",
    100: "#b3e5fc",
    300: "#4fc3f7",
    bg: "#e5f6fd",
    light: "#29b6f6",
    main: "#03a9f4",
    dark: "#0288d1",
    "soft-bg": "#dbeafe",
    "soft-text": "#1f5e9e",
    contrast: "#ffffff",
  },
  orange: {
    A400: "#ff9100",
  },
} as const;

export const semanticColors = {
  background: {
    primary: "#ffffff",
    secondary: "#fafafa",
    tertiary: "#f5f5f5",
    surface: "#ffffff",
  },
  text: {
    primary: "#222222",
    secondary: "#5c5c5c",
    tertiary: "#717171",
    disabled: "#9c9c9c",
    inverse: "#ffffff",
  },
  border: {
    default: "#e5e5e5",
    light: "#eeeeee",
    dark: "#5c5c5c",
  },
  status: {
    success: "#006b3f",
    error: "#b91c1c",
    warning: "#d4580a",
    info: "#1f5e9e",
  },
  interactive: {
    primary: "#6b21a8",
    primaryLight: "#f4ecfb",
    primaryDark: "#581c87",
    secondary: "#9c27b0",
    secondaryLight: "#e1bee7",
    secondaryDark: "#7b1fa2",
  },
  disabled: {
    background: "#f5f5f5",
    text: "#9c9c9c",
    border: "#eeeeee",
  },
  chart: {
    series: ["#d500f9", "#2196f3", "#d500f94d", "#2196f34d"],
    serieNames: {
      todayTarget: 0,
      todayActual: 1,
      yesterdayTarget: 2,
      yesterdayActual: 3,
    },
  },
} as const;

export const colors = {
  ...colorPrimitive,
  ...semanticColors,
} as const;

export type SemanticColors = typeof semanticColors;
export type ColorPrimitive = typeof colorPrimitive;

export const palette = {
  primary: {
    light: "#f4ecfb",
    main: "#6b21a8",
    dark: "#581c87",
    contrastText: "#f4f1ec",
  },
  secondary: {
    light: "#e1bee7",
    main: "#9c27b0",
    dark: "#7b1fa2",
    contrastText: "#ffffff",
  },
  error: {
    light: "#ef5350",
    main: "#f44336",
    dark: "#d32f2f",
    contrastText: "#ffffff",
  },
  warning: {
    light: "#ffb74d",
    main: "#ff9800",
    dark: "#f57c00",
    contrastText: "#ffffff",
  },
  info: {
    light: "#29b6f6",
    main: "#03a9f4",
    dark: "#0288d1",
    contrastText: "#ffffff",
  },
  success: {
    light: "#81c784",
    main: "#4caf50",
    dark: "#388e3c",
    contrastText: "#ffffff",
  },
  text: {
    primary: "#222222",
    secondary: "#5c5c5c",
    disabled: "#9c9c9c",
  },
  background: {
    default: "#fafafa",
    paper: "#ffffff",
    dark: "rgba(0, 0, 0, 0.05)",
  },
  divider: "#e5e5e5",
  action: {
    active: "#616161",
    hover: "rgba(0, 0, 0, 0.04)",
    selected: "rgba(0, 0, 0, 0.08)",
    disabled: "#bdbdbd",
    disabledBackground: "#eeeeee",
  },
} as const;

export type Palette = typeof palette;

export const spacing = {
  xxs: 2,
  xs: 4,
  "xs-sm": 6,
  sm: 8,
  "sm-md": 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  "3xl": 64,
  "4xl": 80,
  gutter: 16,
  section: 24,
} as const;

export type Spacing = typeof spacing;

export const typography = {
  fontSizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
  },
  fontWeights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeights: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
  letterSpacings: {
    tight: "-0.02em",
    normal: "0em",
    wide: "0.05em",
    wider: "0.1em",
  },
} as const;

export type Typography = typeof typography;

export const shadows = {
  none: "none",
  sm: "0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)",
  md: "0px 3px 6px rgba(0, 0, 0, 0.15), 0px 2px 4px rgba(0, 0, 0, 0.12)",
} as const;

export type Shadows = typeof shadows;

export const borders = {
  divider: "1px solid #e5e5e5",
} as const;

export type Borders = typeof borders;

export const zIndex = {
  default: 0,
  elevated: 10,
  stickyHeader: 100,
  appBar: 100,
  modal: 1000,
  dialog: 1000,
  drawer: 1050,
  tooltip: 2000,
  popover: 2000,
  snackbar: 2100,
} as const;
